import {
  isBefore,
  addMinutes,
  startOfDay,
  addDays,
  isEqual,
  parseISO,
  format,
  Duration,
  formatDuration as fnsFormatDuration,
} from 'date-fns'
import {
  DateTimeFormatter,
  Instant,
  LocalDateTime,
  nativeJs,
  ZonedDateTime,
  ZoneId,
} from '@js-joda/core'
import '@js-joda/timezone'
import { RoundMode } from 'shared/components/DatePicker'
import { TFunction } from 'react-i18next'
import { getDateFnsLocale } from './locale'

const DEFAULT_PATTERN = 'd.MM.yyyy HH:mm' //for js-joda
const DEFAULT_DATE_PATTERN = 'd.MM.yyyy'

const DAY_OF_WEEK_PATTERN = 'E' //for date-fns

const getSafeZoneId = (timezone?: string | null) => {
  const normalizedTimezone = timezone?.trim()
  const fallbackTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  return ZoneId.of(normalizedTimezone || fallbackTimezone)
}

export const generateMinuteIntervals = (
  options: {
    min?: Date
    max?: Date
    interval?: number
  } = {}
): Date[] => {
  const {
    min = startOfDay(new Date()),
    max = startOfDay(addDays(new Date(), 1)),
    interval = 15,
  } = options

  const timeIntervals: Date[] = [min]
  let timeInterval = addMinutes(min, interval)
  while (isEqual(timeInterval, max) || isBefore(timeInterval, max)) {
    timeIntervals.push(timeInterval)
    timeInterval = addMinutes(timeInterval, interval)
  }
  return timeIntervals
}

export const dateToLocalString = (date: Date) => {
  return LocalDateTime.from(nativeJs(date)).toString()
}

export const convertToTimezoneDate = (date: string, timezone: string) => {
  const localDate = LocalDateTime.parse(date)
  const timeZoneId = getSafeZoneId(timezone)
  const zonedDate = localDate.atZone(timeZoneId)
  return zonedDate.toLocalDateTime()
}

export const zonedNowDateTime = (timezone: string) =>
  ZonedDateTime.ofInstant(Instant.now(), getSafeZoneId(timezone))

export const convertToTimezoneDateString = (
  date: string,
  timezone: string,
  config?: {
    onlyDate?: boolean
    hideOffset?: boolean
    dayOffset?: number
  }
) => {
  const localDate = LocalDateTime.parse(date)
  const timeZoneId = getSafeZoneId(timezone)
  let zonedDate = localDate.atZone(timeZoneId)
  if (config?.dayOffset) zonedDate = zonedDate.plusDays(config.dayOffset)
  const formatedDate = zonedDate.format(
    DateTimeFormatter.ofPattern(
      config?.onlyDate ? DEFAULT_DATE_PATTERN : DEFAULT_PATTERN
    )
  )
  let parsedDate = parseISO(date)
  if (config?.dayOffset) parsedDate = addDays(parsedDate, config?.dayOffset)
  const dayOfWeek = format(parsedDate, DAY_OF_WEEK_PATTERN)
  const timeOffset = zonedDate.format(DateTimeFormatter.ofPattern('x'))
  const displayDate = config?.hideOffset
    ? `${dayOfWeek} ${formatedDate}`
    : `${dayOfWeek} ${formatedDate} (GMT${timeOffset})`
  return displayDate
}

export const stringToLocalDate = (date: string) => {
  return new Date(date)
}

export const dateToTimezoneDateString = (
  date: Date,
  timezone: string,
  onlyDate: boolean = false,
  hideTimeOffset?: boolean
) => {
  const localDate = LocalDateTime.from(nativeJs(date))
  const timeZoneId = getSafeZoneId(timezone)
  const zonedDate = localDate.atZone(timeZoneId)
  const formatedDate = zonedDate.format(
    DateTimeFormatter.ofPattern(
      onlyDate ? DEFAULT_DATE_PATTERN : DEFAULT_PATTERN
    )
  )
  const dayOfWeek = format(date, DAY_OF_WEEK_PATTERN)
  const timeOffset = zonedDate.format(DateTimeFormatter.ofPattern('x'))
  const displayDate = hideTimeOffset
    ? `${dayOfWeek} ${formatedDate}`
    : `${dayOfWeek} ${formatedDate} (GMT${timeOffset})`
  return displayDate
}

export const dateToFormattedString = (
  date: Date,
  pattern: string = DEFAULT_PATTERN
) => {
  const localDate = LocalDateTime.from(nativeJs(date))
  const formatedDate = localDate.format(DateTimeFormatter.ofPattern(pattern))
  return formatedDate
}

export const convertDateToISOString = (date: Date) => {
  date.setSeconds(0, 0)
  return date.toISOString()
}

export const roundUp = (intervalMilliseconds: number, datetime?: Date) => {
  datetime = datetime || new Date()
  var modTicks = datetime.getTime() % intervalMilliseconds
  var delta = modTicks === 0 ? 0 : datetime.getTime() - modTicks
  delta += intervalMilliseconds
  return new Date(delta)
}

export const roundDown = (intervalMilliseconds: number, datetime?: Date) => {
  datetime = datetime || new Date()
  var modTicks = datetime.getTime() % intervalMilliseconds
  var delta = modTicks === 0 ? 0 : datetime.getTime() - modTicks
  return new Date(delta)
}

export const round = (intervalMilliseconds: number, datetime?: Date) => {
  datetime = datetime || new Date()
  var modTicks = datetime.getTime() % intervalMilliseconds
  var delta = modTicks === 0 ? 0 : datetime.getTime() - modTicks
  var shouldRoundUp = modTicks > intervalMilliseconds / 2
  delta += shouldRoundUp ? intervalMilliseconds : 0
  return new Date(delta)
}

export const roundToDate = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)

export const onChangeWithStep = (
  date: Date | null,
  step: { minutesStep: number; roundMode: RoundMode },
  onChange: (date: Date | null) => void
): Date | null => {
  if (date) {
    if (step.roundMode === RoundMode.Daily) {
      const changed = roundToDate(date)
      onChange(roundToDate(changed))
      return changed
    }
    const stepValue = step.minutesStep * 60 * 1000
    if (date.getTime() % stepValue !== 0) {
      switch (step.roundMode) {
        case RoundMode.Down: {
          const changed = roundDown(stepValue, date)
          onChange(changed)
          return changed
        }
        case RoundMode.Up: {
          const changed = roundUp(stepValue, date)
          onChange(changed)
          return changed
        }
        case RoundMode.Nearest: {
          const changed = round(stepValue, date)
          onChange(changed)
          return changed
        }
      }
    } else {
      onChange(date)
      return date
    }
  } else onChange(null)
  return null
}

export const convertLocalDateTimeForFilename = (datetime: Date) => {
  const year = datetime.getFullYear()
  const month = String(datetime.getMonth() + 1).padStart(2, '0')
  const day = String(datetime.getDate()).padStart(2, '0')
  const hours = String(datetime.getHours()).padStart(2, '0')
  const minutes = String(datetime.getMinutes()).padStart(2, '0')
  const seconds = String(datetime.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`
}

export const formatDuration = (
  duration: Duration,
  t: TFunction<'common', undefined>
): string =>
  fnsFormatDuration(duration, {
    locale: getDateFnsLocale(t),
  })

export const formatDurationUnit = (
  unit: 'day' | 'hour' | 'minute',
  count: number,
  t: TFunction<'common', undefined>
): string =>
  formatDuration(
    {
      [`${unit}s`]: count,
    } as Duration,
    t
  )

export const areSameDateValues = (
  left: Date | null,
  right: Date | null
): boolean => {
  if (left === null && right === null) return true
  if (left === null || right === null) return false

  return isEqual(left, right)
}

export type DateRangeRow = {
  start: Date | null
  end: Date | null
}

export const getSharedDateRange = <T extends DateRangeRow>(rows: T[]) => {
  const first = rows[0]
  if (!first) {
    return {
      start: null as Date | null,
      end: null as Date | null,
      allHaveSameDates: false,
    }
  }

  const allHaveSameStart = rows.every(row =>
    areSameDateValues(row.start, first.start)
  )
  const allHaveSameEnd = rows.every(row =>
    areSameDateValues(row.end, first.end)
  )

  return {
    start: allHaveSameStart ? first.start : null,
    end: allHaveSameEnd ? first.end : null,
    allHaveSameDates: allHaveSameStart && allHaveSameEnd,
  }
}

export type DateValidationMessages = {
  maxFromDateMessage: string
  minToDateMessage: string
}

export type DateFieldRequirements = {
  startRequired?: boolean
  endRequired?: boolean
  requiredMessage: string
  showRequiredErrors?: boolean
}

export const getValidatedDateFields = (
  nextStart: Date | null,
  nextEnd: Date | null,
  messages: DateValidationMessages,
  requirements?: DateFieldRequirements
) => {
  const startMissing = Boolean(requirements?.startRequired && nextStart == null)
  const endMissing = Boolean(requirements?.endRequired && nextEnd == null)

  if (startMissing || endMissing) {
    const showRequiredErrors = requirements?.showRequiredErrors !== false

    return {
      start: nextStart,
      end: nextEnd,
      startErrorMessage:
        showRequiredErrors && startMissing
          ? requirements!.requiredMessage
          : undefined,
      endErrorMessage:
        showRequiredErrors && endMissing
          ? requirements!.requiredMessage
          : undefined,
    }
  }

  if (nextStart && nextEnd) {
    const isValid = nextStart < nextEnd

    return {
      start: nextStart,
      end: nextEnd,
      startErrorMessage: isValid ? undefined : messages.maxFromDateMessage,
      endErrorMessage: isValid ? undefined : messages.minToDateMessage,
    }
  }

  return {
    start: nextStart,
    end: nextEnd,
    startErrorMessage: undefined as string | undefined,
    endErrorMessage: undefined as string | undefined,
  }
}

export const hasDateFieldErrors = (fields: {
  startErrorMessage?: string
  endErrorMessage?: string
}) =>
  fields.startErrorMessage !== undefined || fields.endErrorMessage !== undefined

export const getFirstDateRangeErrorMessages = <
  T extends {
    startErrorMessage?: string
    endErrorMessage?: string
  }
>(
  rows: T[],
  excludedMessage?: string
) => ({
  startErrorMessage: rows.find(
    row =>
      row.startErrorMessage &&
      (!excludedMessage || row.startErrorMessage !== excludedMessage)
  )?.startErrorMessage,
  endErrorMessage: rows.find(
    row =>
      row.endErrorMessage &&
      (!excludedMessage || row.endErrorMessage !== excludedMessage)
  )?.endErrorMessage,
})

export const instantToTimezoneDateString = (
  value: string | null | undefined,
  timezone: string
): string => {
  if (!value) return '-'

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return '-'

  return LocalDateTime.from(nativeJs(date, getSafeZoneId(timezone))).format(
    DateTimeFormatter.ofPattern(DEFAULT_PATTERN)
  )
}
